import { useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { scrollTo } from "seamless-scroll-polyfill";

import AscFlashCard from "../AscCards/AscFlashCard/AscFlashCard";
import styles from "./AscFlashcardActivity.module.scss";
import { ClassicChevronLeftIcon, ClassicChevronRightIcon } from "../AscIcons";
import AscButton from "../AscButton/AscButton";
import AscProgressBar from "../AscProgressBar/AscProgressBar";
import AscCorrectAnswersCard from "../AscCards/AscCorrectAnswersCard/AscCorrectAnswersCard";
export interface AscFlashcardQuestion {
  id: string;
  question: string;
  coverImageUrl?: string;
  correctAnswer?: string;
  correct?: boolean;
  answer?: string;
}

export interface AscFlashcardActivityProps {
  className?: string;
  questions: AscFlashcardQuestion[];
  questionLabel?: string;
  onAnswerChange?: (questionId: string, answer: string) => void;
  disabled?: boolean;
  completed?: boolean;
  onAttempt?: () => void;
  onTryAgain?: () => void;
  loading?: boolean;
  activityLoading?: boolean;
  completedActivity?: boolean;
  showCorrectAnswers?: boolean;
  attemptsCompleted?: boolean;
}

export default function AscFlashcardActivity({
  className,
  questions,
  questionLabel = "Question",
  onAnswerChange,
  disabled = false,
  completed = false,
  loading = false,
  completedActivity,
  activityLoading,
  onAttempt,
  onTryAgain,
  showCorrectAnswers,
}: AscFlashcardActivityProps) {
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cardsContainerRef = useRef<HTMLDivElement>(null);
  const answeredQuestions = useMemo(
    () =>
      completed
        ? questions?.length
        : questions?.filter((question) => question?.answer).length,
    [questions, completed]
  );

  function onPrevious() {
    if (activeQuestionIndex === 0) {
      return;
    }
    setActiveQuestionIndex(activeQuestionIndex - 1);
  }

  function onNext() {
    if (activeQuestionIndex === questions?.length - 1) {
      return;
    }
    setActiveQuestionIndex(activeQuestionIndex + 1);
  }

  function scrollToElement(
    container: HTMLElement,
    card: HTMLElement,
    smoothing = true
  ): void {
    const containerWidth = container.clientWidth;
    const cardWidth = card.clientWidth;
    const offset = (containerWidth - cardWidth) / 2;

    const position = card.offsetLeft - container.offsetLeft - offset;

    scrollTo(container, {
      left: position,
      behavior: smoothing ? "smooth" : undefined,
    });
  }

  function onNavigate(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "ArrowLeft") {
      onPrevious();
    }
    if (event.key === "ArrowRight") {
      onNext();
    }
  }

  function evaluateState(correct: boolean | undefined) {
    if (correct === undefined) {
      return "default";
    }
    return correct ? "correct" : "incorrect";
  }

  useEffect(() => {
    const cardElement = cardRefs?.current?.[activeQuestionIndex];
    if (!cardElement || !cardsContainerRef?.current) {
      return;
    }
    scrollToElement(cardsContainerRef?.current, cardElement);
  }, [activeQuestionIndex]);

  useEffect(() => {
    // if the question is not answered, we don't want to change the active question
    if (questions?.[activeQuestionIndex]?.correct === undefined) {
      return;
    }
    // find the next unanswered question
    const nextQuestionIndex = questions.findIndex(
      (question, index) =>
        index > activeQuestionIndex && question.correct === undefined
    );
    if (nextQuestionIndex !== -1) {
      setActiveQuestionIndex(nextQuestionIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions]);

  useEffect(() => {
    if (loading) {
      setActiveQuestionIndex(0);
    }
  }, [loading]);

  const numberCorrectAnswers = useMemo(
    () => questions.filter((question) => question.correct).length,
    [questions]
  );

  return (
    <div
      data-testid="asc-flashcard-activity"
      className={classNames(styles.ground, className)}
    >
      <div className={styles.header}>
        {questionLabel}: {`${activeQuestionIndex + 1}/${questions?.length}`}
      </div>
      <div
        className={styles.questionsList}
        ref={cardsContainerRef}
        onKeyDown={onNavigate}
      >
        <div className={styles.innerContainer}>
          {questions?.map((question, index) => (
            <AscFlashCard
              loading={loading}
              className={styles.card}
              ref={(ref) => {
                if (cardRefs.current) {
                  cardRefs.current[index] = ref;
                }
              }}
              onAnswerChange={onAnswerChange}
              onClick={() => setActiveQuestionIndex(index)}
              id={question?.id}
              key={question?.id}
              answerLoading={
                question?.id === questions[activeQuestionIndex]?.id
              }
              disabled={disabled || completed}
              title={question?.question}
              coverImageUrl={question?.coverImageUrl}
              state={evaluateState(question?.correct)}
              answer={question?.answer}
              correctAnswer={question?.correctAnswer}
              focused={question?.id === questions[activeQuestionIndex]?.id}
            />
          ))}
        </div>
      </div>
      <div className={styles.navigation}>
        <AscButton
          variant="lightGray"
          className={classNames(styles.previousButton, {
            [styles.hidden]: activeQuestionIndex === 0,
          })}
          icon={<ClassicChevronLeftIcon />}
          disabled={activeQuestionIndex === 0}
          onClick={onPrevious}
          squarishIcon
        />

        <AscProgressBar
          className={styles.progressBar}
          value={answeredQuestions}
          max={questions?.length}
        />

        <AscButton
          variant="lightGray"
          className={classNames(styles.nextButton, {
            [styles.hidden]: activeQuestionIndex === questions?.length - 1,
          })}
          icon={<ClassicChevronRightIcon />}
          squarishIcon
          disabled={activeQuestionIndex === questions?.length - 1}
          onClick={onNext}
        />
      </div>
      <AscCorrectAnswersCard
        type={"secondary"}
        className={styles.correctAnswersCard}
        loading={activityLoading || loading}
        attemptsCompleted={completed}
        onAttempt={onAttempt}
        shouldGoNext={activeQuestionIndex !== questions?.length - 1}
        onNext={onNext}
        onTryAgain={onTryAgain}
        incorrectText={`Incorrect ${questions.length - numberCorrectAnswers}/${
          questions.length
        }`}
        checkAnswerText={
          activeQuestionIndex === questions?.length - 1 ? "Submit" : "Next"
        }
        tryAgainText="Try again"
        disabled={
          loading ||
          completedActivity ||
          completed ||
          (!questions?.every((a) => a?.answer) &&
            activeQuestionIndex === questions?.length - 1)
        }
        correct={questions?.every((a) => a?.correct)}
        showCorrectAnswers={showCorrectAnswers}
      />
    </div>
  );
}
